import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Role } from '@ambec/shared';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { CargaPagina } from './components/Spinner';
import { SelectorModo } from './components/SelectorModo';
import Login from './pages/Login';
import PrivateLayout from './layouts/PrivateLayout';
import AdminLayout from './layouts/AdminLayout';
import Panel from './pages/Panel';
import Clientes from './pages/Clientes';
import Newsletter from './pages/Newsletter';
import CorridaEnVivo from './pages/newsletter/CorridaEnVivo';
import EnConstruccionAgente from './pages/EnConstruccionAgente';
import AgenteDia from './pages/AgenteDia';
import InvestigadorSeia from './pages/InvestigadorSeia';
import ProyectoDia from './pages/ProyectoDia';
import EntrenarAndres from './pages/EntrenarAndres';
import NavegadorSharePoint from './pages/NavegadorSharePoint';
import PreviewCapitulo from './pages/PreviewCapitulo';
import MiPerfil from './pages/MiPerfil';
import Usuarios from './pages/admin/Usuarios';
import Conectores from './pages/admin/Conectores';
import ConversacionesDia from './pages/admin/ConversacionesDia';
import FormatoDocumentos from './pages/admin/FormatoDocumentos';

/** Home privada según el rol de la persona. */
function homePorRol(role: Role): string {
  return role === 'admin' ? '/admin' : '/panel';
}

/** Pantalla neutra mientras se resuelve la sesión inicial. */
function PantallaCarga() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-white">
      <CargaPagina />
    </div>
  );
}

/**
 * Guarda de rutas privadas: exige sesión con perfil validado por el backend y,
 * si se indica `role`, redirige a la home del rol real cuando no coincide.
 */
function RequireAuth({ role, children }: { role?: Role; children: ReactNode }) {
  const { session, profile, loading, rolActivo } = useAuth();

  if (loading) return <PantallaCarga />;
  if (!session || !profile) return <Navigate to="/login" replace />;
  const efectivo = rolActivo ?? profile.role; // rol con el que navega (para admin, el modo elegido)
  if (role && efectivo !== role) {
    return <Navigate to={homePorRol(efectivo)} replace />;
  }
  return <>{children}</>;
}

/** /login es pública, pero con sesión válida redirige a la home del rol EFECTIVO. */
function RutaLogin() {
  const { profile, loading, rolActivo } = useAuth();

  if (loading) return <PantallaCarga />;
  if (profile) return <Navigate to={homePorRol(rolActivo ?? profile.role)} replace />;
  return <Login />;
}

/** Comodín: a la home del rol efectivo si hay sesión válida; si no, a /login. */
function RutaComodin() {
  const { profile, loading, rolActivo } = useAuth();

  if (loading) return <PantallaCarga />;
  return <Navigate to={profile ? homePorRol(rolActivo ?? profile.role) : '/login'} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Contenido />
    </AuthProvider>
  );
}

/** Un ADMIN debe elegir su modo (admin/usuario) antes de entrar; se le pregunta en cada login. */
function Contenido() {
  const { loading, profile, esAdmin, rolActivo, elegirModo } = useAuth();
  if (!loading && profile && esAdmin && rolActivo === null) {
    return <SelectorModo nombre={profile.full_name} onElegir={elegirModo} />;
  }
  return (
    <Routes>
        <Route path="/login" element={<RutaLogin />} />

        {/* Área privada (rol user): layout con sidebar + outlet. */}
        <Route
          element={
            <RequireAuth role="user">
              <PrivateLayout />
            </RequireAuth>
          }
        >
          <Route path="/panel" element={<Panel />} />
          <Route path="/newsletter" element={<Newsletter />} />
          <Route path="/newsletter/corrida/:jobId" element={<CorridaEnVivo />} />
          <Route path="/clientes" element={<Clientes />} />
          <Route
            path="/agentes/prefactibilidad"
            element={<EnConstruccionAgente nombre="Prefactibilidad" descripcion="evaluar tempranamente la viabilidad ambiental de un proyecto, antes de comprometerlo con el SEIA" />}
          />
          <Route path="/sharepoint" element={<NavegadorSharePoint />} />
          <Route path="/perfil" element={<MiPerfil />} />
          <Route path="/agentes/dia" element={<AgenteDia />} />
          <Route path="/agentes/dia/entrenar" element={<EntrenarAndres />} />
          <Route path="/agentes/dia/:favoritoId" element={<ProyectoDia />} />
          <Route path="/agentes/investigador-seia" element={<InvestigadorSeia />} />
          <Route
            path="/agentes/eia"
            element={<EnConstruccionAgente nombre="EIA" descripcion="abordar Estudios de Impacto Ambiental completos junto al equipo de agentes" />}
          />
        </Route>

        {/* Vista-documento a pantalla completa (sin sidebar): previsualización en vivo de
            un capítulo, pensada para abrirse en otra pestaña mientras se edita. */}
        <Route
          path="/agentes/dia/:favoritoId/capitulo/:capitulo/preview"
          element={
            <RequireAuth role="user">
              <PreviewCapitulo />
            </RequireAuth>
          }
        />

        {/* Área de administración (rol admin). */}
        <Route path="/admin" element={<Navigate to="/admin/usuarios" replace />} />
        <Route
          element={
            <RequireAuth role="admin">
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route path="/admin/usuarios" element={<Usuarios />} />
          <Route path="/admin/conectores" element={<Conectores />} />
          <Route path="/admin/formato-documentos" element={<FormatoDocumentos />} />
          <Route path="/admin/conversaciones-dia" element={<ConversacionesDia />} />
        </Route>
        <Route path="*" element={<RutaComodin />} />
      </Routes>
  );
}
